import { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { COUNTRIES } from '@/lib/countries';
import { Globe } from 'lucide-react';

/**
 * Compact country filter for list pages (Feb 2026). Auto-selects the
 * caller's default country on first mount, then reports the choice
 * back via ``onChange``. ``value`` may be a country name or the string
 * ``'all'`` (to disable filtering).
 *
 * Renders as a small pill so it stays out of the way while all data
 * is single-country. Grows in utility the moment a second country
 * appears.
 */
export function CountryFilter({ value, onChange, testId }) {
  const [defaultCountry, setDefaultCountry] = useState(null);

  useEffect(() => {
    // Only pre-fill when the parent hasn't already set a value.
    if (value) return;
    let cancelled = false;
    axios.get(`${API}/countries/my-default`, { withCredentials: true })
      .then(r => {
        if (cancelled) return;
        const c = r.data?.country || 'New Zealand';
        setDefaultCountry(c);
        onChange(c);
      })
      .catch(() => {
        if (cancelled) return;
        setDefaultCountry('New Zealand');
        onChange('New Zealand');
      });
    return () => { cancelled = true; };
  }, [value, onChange]);

  return (
    <div className="inline-flex items-center gap-2 text-sm text-muted-foreground" data-testid={testId}>
      <Globe className="h-4 w-4" />
      <span>Country:</span>
      <select
        value={value || defaultCountry || ''}
        onChange={(e) => onChange(e.target.value)}
        className="border rounded-lg px-2 py-1 bg-white text-stone-900"
        data-testid={`${testId}-select`}
      >
        <option value="all">All countries</option>
        {COUNTRIES.map(c => (
          <option key={c} value={c}>{c}</option>
        ))}
      </select>
    </div>
  );
}
